// What is lapsing on this person, on their Overview.
//
// RENDERED ONLY WHEN IT HAS SOMETHING IN IT. There is deliberately no "all
// current" green counterpart: a banner that is present every time you open the
// page is a banner people stop reading, and the one time it changes colour is
// the one time that matters. Zanetto Tools learned this the same way round.
//
// Each row jumps to the folder holding the record rather than just naming it,
// because "Sofia's forklift ticket expires in 24 days" is only useful next to
// the thing you renew.
import type { PersonRecord } from '../../peopleApi';
import { StatusBadge } from '../ui';
import { formatDMY } from '../../lib/dates';

/**
 * Expired first, then soonest.
 *
 * Sorting on the ISO date does both at once — an expired ticket is simply an
 * earlier date — so there is no separate "overdue" branch to keep in step.
 */
export function needingAttention(records: PersonRecord[]): PersonRecord[] {
  return records
    .filter((r) => r.expiry === 'expired' || r.expiry === 'soon')
    .sort((a, b) => (a.expiresOn ?? '').localeCompare(b.expiresOn ?? ''));
}

export function NeedsAttentionCard({
  records,
  isSelf,
  onJumpTo,
}: {
  records: PersonRecord[];
  /** Changes the voice, not the content. */
  isSelf?: boolean;
  onJumpTo: (record: PersonRecord) => void;
}) {
  const items = needingAttention(records);
  if (items.length === 0) return null;

  return (
    <section className="panel attention">
      <h2>{isSelf ? 'Your records needing attention' : 'Needs attention'}</h2>
      <ul className="m-0 list-none space-y-1 p-0">
        {items.map((record) => (
          <li key={record.id}>
            <button
              type="button"
              className="flex min-h-11 w-full flex-wrap items-center gap-2 text-left"
              onClick={() => onJumpTo(record)}
            >
              <span className="font-medium">{record.title}</span>
              <StatusBadge kind="expiry" status={record.expiry} />
              {record.expiresOn && (
                <span className="text-sm text-gray-600">{formatDMY(record.expiresOn)}</span>
              )}
            </button>
          </li>
        ))}
      </ul>
      {isSelf && (
        <p className="hint">
          Renewals are recorded by the office; let them know once you have done one.
        </p>
      )}
    </section>
  );
}
